/**
 * OutreachJobPausedModal
 *
 * One-shot modal that pops up in the WorldDb whenever an Outreach Job hits a
 * fatal error and is auto-paused by the backend (`pauseJobWithError`).
 *
 * DATA SOURCE:
 *   RTDB /World/{userId}/alerts, filtered to type === 'outreach_job_paused'
 *   and !dismissed. Each pause emits one such alert; dedup happens server-side
 *   in `alertHelper.createAlert` via the per-job `dedupKey`.
 *
 * TRIGGER MODEL — "once per fatal error":
 *   Dismissed alerts are never re-shown, even after a page reload. Clicking
 *   either button flips `dismissed: true` on the RTDB record, so the modal
 *   stays gone until the job hits ANOTHER fresh fatal (which writes a new
 *   alert). This is intentional — the pause banner inside the job detail is
 *   what remains for the "please fix this" reminder.
 *
 * SHOWING ONE AT A TIME:
 *   If several fatals stack up (rare — dedup usually collapses them), we
 *   surface the newest one first. Dismissing it reveals the next one on the
 *   following render tick, so the user isn't hit with N stacked modals.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { database } from '../firebaseConfig';
import { ref, onValue, update } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import './OutreachJobPausedModal.css';

const OutreachJobPausedModal = ({ userId }) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    if (!userId) {
      setAlerts([]);
      return undefined;
    }
    const alertsRef = ref(database, `World/${userId}/alerts`);
    const unsub = onValue(alertsRef, (snap) => {
      const val = snap.val();
      if (!val) {
        setAlerts([]);
        return;
      }
      const now = Date.now();
      const list = Object.entries(val)
        .map(([id, a]) => ({ id, ...a }))
        .filter((a) => a.type === 'outreach_job_paused')
        .filter((a) => !a.dismissed)
        .filter((a) => !a.expiresAt || a.expiresAt > now)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setAlerts(list);
    });
    return () => unsub();
  }, [userId]);

  const current = alerts[0] || null;

  const dismissCurrent = useCallback(() => {
    if (!userId || !current) return;
    // Optimistic — hide immediately, then persist so a slow RTDB write doesn't
    // let the modal linger on screen.
    setAlerts((prev) => prev.filter((a) => a.id !== current.id));
    update(ref(database, `World/${userId}/alerts/${current.id}`), {
      dismissed: true,
      dismissedAt: Date.now(),
    }).catch(() => {});
  }, [userId, current]);

  const openJob = useCallback(() => {
    if (!current) return;
    const jobId = current.metadata?.jobId || '';
    // Hand the target job to OutreachJobsSettings so it opens straight into
    // the detail view instead of forcing the user to scroll a possibly long
    // job list to find the paused one.
    if (jobId) {
      try {
        sessionStorage.setItem('openOutreachJobId', jobId);
      } catch (_) {}
    }
    // Same pattern already used by AiCleanerPopup → settings.
    try {
      sessionStorage.setItem('openSettingsTab', 'outreachJobs');
    } catch (_) {}
    document.dispatchEvent(new CustomEvent('openSettingsTab', { detail: { tab: 'outreachJobs' } }));
    dismissCurrent();
    navigate('/world?view=settings&tab=outreachJobs');
  }, [current, dismissCurrent, navigate]);

  const isPlatformCredits = current?.metadata?.pauseReason === 'platform_credits_exhausted';
  const reasonLabel = useMemo(() => {
    const reason = current?.metadata?.pauseReason || '';
    if (reason === 'platform_credits_exhausted') return 'No system credits';
    return reason ? reason.replace(/_/g, ' ') : '';
  }, [current]);

  if (!current) return null;

  const jobName = current.metadata?.jobName || current.metadata?.jobId || 'Outreach Job';
  const message = current.message || 'This outreach job was paused because of a fatal error.';

  return (
    <div
      className="ojp-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ojp-modal-title"
      onClick={(e) => {
        // Backdrop click = dismiss (same as pressing "Close"). We treat
        // an outside click as an acknowledged dismissal because the alert is
        // deliberately one-shot; the pause banner in the job stays there as
        // the persistent reminder.
        if (e.target === e.currentTarget) dismissCurrent();
      }}
    >
      <div className="ojp-modal-sheet">
        <div className="ojp-modal-header">
          <div className="ojp-modal-icon-wrap">
            <i className="fa fa-exclamation-circle" aria-hidden="true" />
          </div>
          <div className="ojp-modal-header-copy">
            <div className="ojp-modal-eyebrow">Outreach job paused</div>
            <h2 id="ojp-modal-title" className="ojp-modal-title">{jobName}</h2>
            {reasonLabel && (
              <div className="ojp-modal-reason-chip">
                <i className="fa fa-circle-exclamation" aria-hidden="true" />
                <span>{reasonLabel}</span>
              </div>
            )}
          </div>
          <button
            className="ojp-modal-close"
            onClick={dismissCurrent}
            aria-label="Close"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="ojp-modal-body">
          <p className="ojp-modal-message">{message}</p>
          <p className="ojp-modal-hint">
            {isPlatformCredits
              ? 'The agent restarts on its own as soon as generation works again. Your contacts stay in the queue.'
              : 'The job stays paused until you fix the issue and resume it.'}
          </p>
        </div>

        <div className="ojp-modal-actions">
          <button
            type="button"
            className="ojp-modal-btn secondary"
            onClick={dismissCurrent}
          >
            Close
          </button>
          <button
            type="button"
            className="ojp-modal-btn primary"
            onClick={openJob}
          >
            <i className="fa fa-arrow-right" aria-hidden="true" />
            <span>Open job</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default OutreachJobPausedModal;
